/**
 * AG Grid column/format helpers for the PnL report grid.
 *
 * Ported from the xPT `pnl-reports/utils/pnl-report-utils.ts`, adapted to FinSuite's
 * AG Grid v35 (the xPT grid used v36 `calculatedColumns` / `calculatedExpression`,
 * which do not exist in v35 — variance columns are instead evaluated by a plain
 * `valueGetter`, exactly the client-side model xPT already used underneath).
 *
 * Pure + React-free by design (CLAUDE.md separation of concerns): builds ColDefs and
 * formats values; owns no state.
 */
import { CellClassParams, ColDef, ColGroupDef, IAggFuncParams, RowClassParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { PnlGridRow, PnlPivotCalcColumn, PnlPivotColumnGroup } from '../starGridTypes';

/**
 * Global (non-module) cell class names applied to AG Grid's own DOM. Non-module so the
 * literal class reaches the grid; styled in styles/pnl-report-grid.scss.
 */
export const PNL_CELL_CLASSES = {
  /** Negative value → finance red. */
  negative: 'sg-pnl-negative',
  /** Alternating tint band on every other scenario/variance column group. */
  band: 'sg-pnl-col-band',
  /** Group total / grand total footer rows. */
  grandTotal: 'sg-pnl-grand-total'
} as const;

// ─── Display scale ────────────────────────────────────────────────────────────

/** Display scale for value columns (à la Prophecy): a pure display divide. */
export interface PnlScaleOption {
  readonly id: string;
  readonly label: string;
  readonly divisor: number;
  readonly decimals: number;
}

export const PNL_SCALE_OPTIONS: readonly PnlScaleOption[] = [
  { id: 'actual', label: '$ (whole)', divisor: 1, decimals: 0 },
  { id: 'thousands', label: "$ in 000's", divisor: 1_000, decimals: 1 },
  { id: 'millions', label: "$ in MM's", divisor: 1_000_000, decimals: 2 }
];

export const PNL_DEFAULT_SCALE: PnlScaleOption = PNL_SCALE_OPTIONS[0];

export const getPnlScaleById = (id: string | undefined): PnlScaleOption => PNL_SCALE_OPTIONS.find((s) => s.id === id) ?? PNL_DEFAULT_SCALE;

/**
 * Format a numeric P&L value: thousands-separated, negatives in parentheses (finance
 * convention), null → em dash. `divisor`/`decimals` apply the selected display scale.
 */
export const formatPnlCurrency = (value: number | null | undefined, divisor = 1, decimals = 0): string => {
  if (value === null || value === undefined) return '—';
  const scaled = divisor === 1 ? value : value / divisor;
  const abs = Math.abs(scaled).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  return scaled < 0 ? `(${abs})` : abs;
};

/** Coerce a row cell (string | number | null) to a finite number or null. */
export const toNumber = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.length > 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

// ─── Value / calculated columns ───────────────────────────────────────────────

const valueFormatter = (params: ValueFormatterParams<PnlGridRow, number | null>): string => {
  const scale = getPnlScaleById(params.context?.scaleId);
  return formatPnlCurrency(params.value, scale.divisor, scale.decimals);
};

const negativeCellClassRules = {
  [PNL_CELL_CLASSES.negative]: (params: CellClassParams<PnlGridRow, number | null>) => typeof params.value === 'number' && params.value < 0
};

/**
 * A numeric (scenario × month) value column. Cells may arrive as decimal strings, so a
 * valueGetter coerces to number first (otherwise SUM would concatenate strings).
 */
const buildNumericColDef = (field: string, headerName: string): ColDef<PnlGridRow> => ({
  field,
  headerName,
  type: 'numericColumn',
  minWidth: 110,
  flex: 1,
  filter: false,
  suppressHeaderMenuButton: true,
  enableValue: true,
  aggFunc: 'pnlSum',
  allowedAggFuncs: ['pnlSum', 'avg', 'min', 'max', 'count'],
  valueGetter: (params: ValueGetterParams<PnlGridRow>): number | null => (params.data ? toNumber(params.data[field]) : null),
  valueFormatter,
  cellClassRules: negativeCellClassRules
});

// Only `+ - * /`, left-to-right, no parens — enough for the variance expressions we emit.
type Token = { type: 'ref'; name: string } | { type: 'op'; op: '+' | '-' | '*' | '/' };

const tokenizeExpression = (expression: string): Token[] | null => {
  const tokens: Token[] = [];
  const re = /\[([^\]]+)\]|([+\-*/])|(\s+)/g;
  let lastEnd = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(expression)) !== null) {
    if (m.index !== lastEnd) return null;
    lastEnd = re.lastIndex;
    if (m[1] !== undefined) {
      tokens.push({ type: 'ref', name: m[1] });
    } else if (m[2] !== undefined) {
      const op = m[2];
      if (op === '+' || op === '-' || op === '*' || op === '/') tokens.push({ type: 'op', op });
      else return null;
    }
  }
  if (lastEnd !== expression.length) return null;
  return tokens;
};

/** Build a valueGetter that evaluates `[A] - [B]` against each row. Null if any operand missing. */
export const buildCalculatedValueGetter = (expression: string): ((params: ValueGetterParams<PnlGridRow>) => number | null) => {
  const tokens = tokenizeExpression(expression);
  if (!tokens || tokens.length === 0 || tokens[0].type !== 'ref') return () => null;
  return (params) => {
    const row = params.data;
    if (!row) return null;
    const firstRef = tokens[0] as Extract<Token, { type: 'ref' }>;
    const first = toNumber(row[firstRef.name]);
    if (first === null) return null;
    let acc = first;
    for (let i = 1; i < tokens.length; i += 2) {
      const opTok = tokens[i];
      const refTok = tokens[i + 1];
      if (!opTok || opTok.type !== 'op' || !refTok || refTok.type !== 'ref') return null;
      const operand = toNumber(row[refTok.name]);
      if (operand === null) return null;
      switch (opTok.op) {
        case '+':
          acc += operand;
          break;
        case '-':
          acc -= operand;
          break;
        case '*':
          acc *= operand;
          break;
        case '/':
          acc = operand === 0 ? NaN : acc / operand;
          break;
      }
    }
    return Number.isFinite(acc) ? acc : null;
  };
};

const buildCalculatedColDef = (col: PnlPivotCalcColumn): ColDef<PnlGridRow> => ({
  colId: col.colId,
  headerName: col.headerName,
  type: 'numericColumn',
  minWidth: 110,
  flex: 1,
  filter: false,
  suppressHeaderMenuButton: true,
  enableValue: true,
  aggFunc: 'pnlSum',
  allowedAggFuncs: ['pnlSum', 'avg', 'min', 'max', 'count'],
  valueGetter: buildCalculatedValueGetter(col.expression),
  valueFormatter,
  cellClassRules: negativeCellClassRules
});

/**
 * Tag a column group + its children with the banding class so the tint lands on the
 * group header, each child header, and each data cell. No-op when `band` is false.
 */
const applyGroupBand = (group: ColGroupDef, band: boolean): ColGroupDef => {
  if (!band) return group;
  return {
    ...group,
    headerClass: PNL_CELL_CLASSES.band,
    children: group.children.map((child) => ({
      ...child,
      headerClass: PNL_CELL_CLASSES.band,
      cellClass: PNL_CELL_CLASSES.band
    }))
  };
};

/**
 * Build the grid column defs from the pivot model: one grouped header per scenario
 * (month value columns), then one grouped header per variance pair. Dimensions are the
 * row axis (tree hierarchy), not columns — they render in the pinned auto-group column.
 */
export const buildColumnDefsFromModel = (
  columnGroups: readonly PnlPivotColumnGroup[],
  calculatedColumns: readonly PnlPivotCalcColumn[]
): (ColDef<PnlGridRow> | ColGroupDef)[] => {
  const result: (ColDef<PnlGridRow> | ColGroupDef)[] = [];
  let bandIndex = 0;
  const banded = (): boolean => bandIndex++ % 2 === 1;

  for (const group of columnGroups) {
    const children = group.columns.map((col) => buildNumericColDef(col.field, col.headerName));
    result.push(applyGroupBand({ headerName: group.group_label, children }, banded()));
  }

  if (calculatedColumns.length > 0) {
    const byGroup = new Map<string, PnlPivotCalcColumn[]>();
    for (const col of calculatedColumns) {
      const arr = byGroup.get(col.group) ?? [];
      arr.push(col);
      byGroup.set(col.group, arr);
    }
    for (const [label, cols] of byGroup) {
      result.push(applyGroupBand({ headerName: label, children: cols.map(buildCalculatedColDef) }, banded()));
    }
  }

  return result;
};

// ─── Row hierarchy (tree data) ────────────────────────────────────────────────

/**
 * The single indented hierarchy column for tree-data rows (Prophecy-style drill). Every
 * dimension level collapses into this one pinned column; drilling adds a deeper path
 * segment rather than a new column. Header names the base grouping dimension(s).
 */
export const buildAutoGroupColumnDef = (dimensionFields: readonly string[], dimensionLabels?: Record<string, string>): ColDef<PnlGridRow> => ({
  headerName: dimensionFields.length ? dimensionFields.map((f) => dimensionLabels?.[f] ?? f).join(' › ') : 'Group',
  minWidth: 260,
  pinned: 'left',
  cellRendererParams: {
    suppressCount: false,
    footerValueGetter: (params: ValueGetterParams<PnlGridRow>): string => {
      const key = params.node?.key;
      return key ? `Total ${key}` : 'Grand Total';
    }
  }
});

/**
 * Tree path for a flat, pre-aggregated row: the ordered dimension values matching
 * `dimensionFields`. AG Grid synthesizes + aggregates the intermediate group rows, so a
 * drill (one extra dimension in group_by) simply deepens every path by one. Blank tail
 * values are dropped so a coarser-grained row lands at the right depth.
 *
 * (xPT's multi-level hierarchy registry — expanding a leaf into its rollup path — is
 * omitted: FinSuite does not yet load the corp-segment hierarchies. Dimensions render
 * flat, one path segment per value.)
 */
export const buildGetDataPath =
  (dimensionFields: readonly string[]): ((row: PnlGridRow) => string[]) =>
  (row) => {
    const path: string[] = [];
    for (const field of dimensionFields) {
      const value = row[field];
      if (value === null || value === undefined || value === '') break;
      path.push(String(value));
    }
    return path.length > 0 ? path : ['—'];
  };

/** Row class rule tagging group/grand-total footer rows so SCSS can distinguish them. */
export const pnlGetRowClass = (params: RowClassParams<PnlGridRow>): string | undefined =>
  params.node.footer ? PNL_CELL_CLASSES.grandTotal : undefined;

/**
 * Sum aggregation that treats an all-null group as null (→ em dash) rather than 0, so an
 * empty group doesn't misread as a real zero balance.
 */
export const pnlSumAggFunc = (params: IAggFuncParams<PnlGridRow, number | null>): number | null => {
  let total: number | null = null;
  for (const value of params.values) {
    if (typeof value === 'number' && Number.isFinite(value)) total = (total ?? 0) + value;
  }
  return total;
};

/** Default column definition for the P&L grid. */
export const pnlDefaultColDef: ColDef<PnlGridRow> = {
  sortable: true,
  resizable: true,
  suppressMovable: false
};
